import Prompt from 'Components/tasks/Prompt/Prompt'
import { useCallback, useState } from 'react'
import {
    ContainerDataType,
    ContainerType,
    ConversationalTurnDataType,
    LinearConversationsDataType,
} from 'Types/Tasks.types'
import './ConversationsTaskUI.less'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import { ViolationsType } from 'shared/violations/types'
import Tooltip from 'antd/lib/tooltip'
import { UserContent } from 'Tasks/ConversationsTask/UI/UserContent/UserContent'
import { ModelContent } from 'Tasks/ConversationsTask/UI/ModelContent/ModelContent'
import { isEmpty } from 'lodash'
import CloseOutlined from '@ant-design/icons/lib/icons/CloseOutlined'
import { useTaskContext } from 'Contexts/TaskContext'
import { ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'
import { IAttributeGroup } from 'Core/AttributeCore/AttributeGroup/BaseAttributeGroup'
import SubmitTurnIcon from 'Assets/icons/arrow-right.svg'

export type ConversationsTaskUIPropsType = {
    getInputDetails: () => ContainerDataType<
        ContainerType.LinearConversation,
        LinearConversationsDataType
    >
    getTurnData: () => ConversationalTurnDataType[]
    isLoading: () => boolean
    submitTurn: (editIndex: number) => void
    getUserMessage: () => string
    updateUserMessage: (message: string) => void
    onClickRegenerateContent: () => void
    getViolation: () => ViolationsType
    isEditorStep?: boolean
    isRetryUnavailable: () => boolean
    isTurnExhausted: () => boolean
    getTurnAttributes: () => IAttributeGroup[]
    blocks: ITaskBlock[]
}

export const ConversationsTaskUI = (props: ConversationsTaskUIPropsType) => {
    const {
        updateUserMessage,
        getTurnData,
        submitTurn,
        isEditorStep,
        isLoading: isDataLoading,
        isRetryUnavailable: isTurnRetryUnavailable,
        onClickRegenerateContent,
        getUserMessage,
        isTurnExhausted: isDataTurnExhausted,
        getInputDetails,
        getViolation,
        getTurnAttributes,
    } = props

    const taskContext = useTaskContext()

    const turnData = getTurnData()
    const isLoading = isDataLoading()
    const isRetryUnavailable = isTurnRetryUnavailable()
    const userMessage = getUserMessage()
    const isTurnExhausted = isDataTurnExhausted()
    const promptData = getInputDetails().data.data.main_info?.value.prompt_instruction ?? ''
    const turnLevelAttributes = getTurnAttributes()
    const errors = Object.values(getViolation().error)

    const [editIndex, setEditIndex] = useState(-1)

    const onClickEditContent = useCallback(
        (index: number) => {
            setEditIndex(index)
            updateUserMessage(turnData.at(index)?.prompt ?? '')
        },
        [turnData, updateUserMessage]
    )

    const onSubmitEditContent = useCallback(() => {
        submitTurn(editIndex)
        setEditIndex(-1)
    }, [editIndex, submitTurn])

    const submitButtonDisabledReason = () => {
        if (isTurnExhausted) {
            return 'You have exhausted your turns for this task'
        }
        if (isLoading) {
            return 'Please wait while we are processing your request'
        }
        if (userMessage.trim() === '') {
            return 'Input is required'
        }

        const filteredErrors = errors.filter(
            error =>
                error.id !== 'attributes' &&
                error.id !== 'turn-attributes' &&
                !error.id.includes('custom_validation') &&
                !error.id.includes('live_feedback')
        )
        if (filteredErrors.length > 0) {
            return filteredErrors[0].message
        }

        return ''
    }

    const buttonDisabledReasons = () => {
        if (editIndex !== -1) {
            return isEmpty(userMessage.trim()) ? 'Input is required' : ''
        } else {
            return submitButtonDisabledReason()
        }
    }

    const onClickCloseButton = () => {
        setEditIndex(-1)
        updateUserMessage('')
    }

    return (
        <div className="conversations-container" data-testid="generation-task-ui-wrapper">
            <Prompt
                title={'INPUT'}
                content={{
                    value: promptData,
                    value_type: 'HTML',
                }}
                taskInfo={taskContext.taskAdditionalInfo}
            />

            <div className="conversations-turns-container">
                {turnData.map((turn: ConversationalTurnDataType, index: number) => (
                    <div className="conversations-turn" key={index}>
                        <UserContent
                            prompt={turn.prompt}
                            onClickEditContent={() => onClickEditContent(index)}
                            isEditAllowed={!!isEditorStep && !isLoading}
                        />
                        <ModelContent
                            modelResponse={turn.modelResponse}
                            isLatestPrompt={index === turnData.length - 1}
                            isLoading={isLoading}
                            onClickRegenerateContent={onClickRegenerateContent}
                            turnLevelAttributes={turnLevelAttributes[index]}
                            isRegenerationAllowed={
                                index === turnData.length - 1 &&
                                !!isEditorStep &&
                                !isRetryUnavailable
                            }
                        />
                    </div>
                ))}
            </div>

            {isEditorStep && (
                <div
                    className={
                        'conversations-input-container' + (editIndex !== -1 ? ' edit-mode' : '')
                    }
                >
                    <div className="input-background" />
                    <div className="input-container">
                        <div className="close-editor-button" onClick={onClickCloseButton}>
                            <CloseOutlined />
                        </div>
                        {props.blocks[0].getElement()?.element}
                        <Tooltip
                            title={buttonDisabledReasons()}
                            data-testid="copy-button-wrapper"
                            placement="top"
                            trigger={'hover'}
                        >
                            <ActionButton
                                title={<img src={SubmitTurnIcon} alt="submit" />}
                                className="submit-turn-button"
                                onClick={onSubmitEditContent}
                                disabled={buttonDisabledReasons().length > 0}
                            />
                        </Tooltip>
                    </div>
                </div>
            )}
        </div>
    )
}
